import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';

const DEFAULT_KPI_SEL = {
	'5G': ['SERVICE DR', 'DATA PRBS USED BY PDSCH', 'AVAILABILITY'],
	'4G': ['VDCR', 'VOICE TRAFFIC', 'AVAILABILITY'],
	'3G': ['VDCR', 'VOICE TRAFFIC', 'AVAILABILITY'],
	'2G': ['VDCR', 'VOICE TRAFFIC', 'AVAILABILITY'],
};

const emptyTechFilters = () => ({
	jvId:      [],
	cellName:  [],
	side:      [],
	polygon:   [],
	vendor:    [],
	manager:   [],
	optimiser: [],
});

const DEFAULT_FILTERS_BY_TECH = {
	'5G': emptyTechFilters(),
	'4G': emptyTechFilters(),
	'3G': emptyTechFilters(),
	'2G': emptyTechFilters(),
};

const defaultDateFrom = () => moment().subtract(1, 'month').format('YYYY-MM-DD');
const defaultDateTo   = () => moment().subtract(1, 'day').format('YYYY-MM-DD');

const filtersSlice = createSlice({
	name: 'filters',
	initialState: {
		// Global filters
		aggLevel:         'Cluster',
		dateFrom:         defaultDateFrom(),
		dateTo:           defaultDateTo(),

		// Per-tech filters: byTech[tech][filterKey] = string[]
		byTech:           DEFAULT_FILTERS_BY_TECH,

		// Tech selection — compare ON by default
		tech1:            '5G',
		tech2:            '4G',

		// KPI selection per tech
		kpiSel:           DEFAULT_KPI_SEL,

		// Legend: per-tech series filtering
		selectedEntities: {},
		numCharts:        3,
	},
	reducers: {
		// ── Global filters ──────────────────────────────────────────────────
		setAggLevel(state, action)   { state.aggLevel  = action.payload; },
		setDateFrom(state, action)   { state.dateFrom  = action.payload; },
		setDateTo(state, action)     { state.dateTo    = action.payload; },

		// ── Per-tech filters ────────────────────────────────────────────────
		setTechFilter(state, action) {
			const { tech, key, value } = action.payload;
			if (!state.byTech[tech]) state.byTech[tech] = emptyTechFilters();
			state.byTech[tech][key] = value;
		},

		clearTechFilters(state, action) {
			const { tech } = action.payload;
			state.byTech[tech] = emptyTechFilters();
		},

		// ── Tech selection ──────────────────────────────────────────────────
		setTech1(state, action)      { state.tech1     = action.payload; },
		setTech2(state, action)      { state.tech2     = action.payload; },

		// ── View / KPI selection ────────────────────────────────────────────
		setNumCharts(state, action)  { state.numCharts = action.payload; },

		setKpiSlot(state, action) {
			const { tech, slot, kpiName } = action.payload;
			state.kpiSel[tech][slot] = kpiName;
		},

		setSelectedEntities(state, action) {
			const { tech, entities } = action.payload;
			if (tech) state.selectedEntities[tech] = entities;
			else      state.selectedEntities = {};
		},

		setLockdownContext(state, action) {
			state.dateFrom = defaultDateFrom();
			state.dateTo   = defaultDateTo();
			if (action.payload?.technology) state.tech1 = action.payload.technology;
		},

		resetFilters(state) {
			state.aggLevel         = 'Cluster';
			state.byTech           = {
				'5G': emptyTechFilters(),
				'4G': emptyTechFilters(),
				'3G': emptyTechFilters(),
				'2G': emptyTechFilters(),
			};
			state.tech2            = '4G';
			state.selectedEntities = {};
		},
	},
});

export const {
	setAggLevel, setDateFrom, setDateTo,
	setTechFilter, clearTechFilters,
	setTech1, setTech2,
	setLockdownContext, setKpiSlot, resetFilters,
	setNumCharts, setSelectedEntities,
} = filtersSlice.actions;

export default filtersSlice.reducer;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectFiltersForTech = (tech) => (state) =>
	state.filters.byTech[tech] ?? {
		jvId: [], cellName: [], side: [], polygon: [],
		vendor: [], manager: [], optimiser: [],
	};

export const selectActiveFilterCountForTech = (tech) => (state) => {
	const f = state.filters.byTech[tech];
	if (!f) return 0;
	return Object.values(f).reduce((acc, arr) => acc + (arr?.length ?? 0), 0);
};
